import type { Strings } from '../index.ts';

/**
 * Was über eine fehlgeschlagene Anfrage zu sagen ist — der gemeinsame Teil, einmal statt einmal pro
 * Bildschirm.
 *
 * **Keine Statuscodes, keine Endpunkte, keine Header-Namen.** Jedes davon ist eine Tatsache darüber, wie
 * Visious und Invidious miteinander reden, und mit keiner davon kann ein Mensch etwas anfangen. Handeln
 * lässt sich über drei Dinge: ist das meine Sache, hilft eine Anmeldung, lohnt ein neuer Versuch. Jeder
 * Satz hier beantwortet die und hört dann auf.
 *
 * `retryable` ist bewusst der Wert aus dem englischen Katalog und keine eigene Entscheidung: der Flag
 * steuert, ob ein „Erneut versuchen“-Knopf erscheint, und das ist Verhalten und keine Übersetzung.
 */
export const problem: Strings['problem'] = {
  auth: {
    notAuthenticated: {
      title: 'Du bist abgemeldet',
      detail:
        'Diese Seite braucht eine Sitzung auf deiner Instanz, und in diesem Browser ist keine gespeichert.',
      retryable: false,
    },
    tokenExpired: {
      title: 'Deine Sitzung ist abgelaufen',
      detail:
        'Sitzungen laufen nach einer Weile ab. Eine neue Anmeldung genügt, und an deinem Konto ändert sich nichts.',
      retryable: false,
    },
    invalidSignature: {
      title: 'Diese Sitzung gilt hier nicht',
      detail:
        'Sie gehört zu einer anderen Instanz, oder sie wurde verändert. Konten gelten pro Instanz, eine neue Anmeldung ersetzt sie also.',
      retryable: false,
    },
    invalidScope: {
      title: 'Diese Sitzung hat dafür keine Berechtigung bekommen',
      detail:
        'An der Anmeldung selbst ist nichts falsch. Sie deckt das hier einfach nicht ab. Neu anmelden und die ganze Liste freigeben ist die Lösung, denn dieselbe Sitzung zu erneuern bringt dieselbe Lücke zurück.',
      retryable: false,
    },
    malformedToken: {
      title: 'Die gespeicherte Sitzung lässt sich nicht lesen',
      detail:
        'Was in diesem Browser gespeichert ist, kann Visious nicht verwenden. Eine neue Anmeldung ersetzt es.',
      retryable: false,
    },
    unrecognised: {
      title: 'Die Instanz hat das abgelehnt',
      detail:
        'Sie hat nicht gesagt, warum, deshalb lohnt sich nur eine neue Anmeldung als Versuch.',
      retryable: false,
    },
  },

  outOfScope: {
    title: 'Diese Sitzung kann das nicht',
    detail:
      'Sie hat dafür keine Berechtigung bekommen, deshalb hat Visious nicht gefragt. Eine neue Anmeldung bittet um den vollen Satz.',
    retryable: false,
  },

  kind: {
    endpointDisabled: {
      title: 'Diese Instanz hat das abgeschaltet',
      detail:
        'Die Administration hat es abgeschaltet, es gibt also nichts zu zeigen und nichts zu wiederholen.',
      retryable: false,
    },
    instanceWarming: {
      title: 'Die Instanz ist beschäftigt',
      detail:
        'Sie ist gerade unter Last. Das legt sich meist innerhalb weniger Sekunden von selbst.',
      retryable: true,
    },
    rateLimited: {
      title: 'Zu viele Anfragen',
      detail: 'Die Instanz bittet um weniger Anfragen. Gib ihr einen Moment und versuche es erneut.',
      retryable: true,
    },
    network: {
      title: 'Die Instanz war nicht erreichbar',
      detail:
        'Es hat überhaupt nichts geantwortet. Prüfe deine Verbindung, oder die Instanz ist gerade aus.',
      retryable: true,
    },
    aborted: {
      title: 'Abgebrochen',
      detail: 'Das wurde abgebrochen, bevor es fertig war.',
      retryable: true,
    },
    upstreamBroken: {
      title: 'YouTube wollte das nicht liefern',
      detail:
        'Deine Instanz hat gefragt und einen Fehler zurückbekommen statt einer Antwort. Ein neuer Versuch bringt dasselbe Ergebnis.',
      retryable: false,
    },
    urlsExpired: {
      title: 'Die Instanz hat diese Anfrage abgelehnt',
      detail:
        'Sie wollte nicht antworten und hat nicht gesagt, warum. Vielleicht weist etwas davor die Anfrage ab.',
      retryable: false,
    },
    server: {
      title: 'Die Instanz ist daran gescheitert',
      detail:
        'Sie ist an dieser Anfrage zerbrochen, und Visious hatte sie schon wiederholt. Der Instanz geht es schlecht, und das hat nichts damit zu tun, wonach du gefragt hast.',
      retryable: true,
    },
    client: {
      title: 'Die Instanz hat diese Anfrage abgewiesen',
      detail:
        'Sie hat verstanden, worum gebeten wurde, und es abgelehnt, was heißt, dass Visious um das Falsche gebeten hat. Das ist ein Fehler hier und nichts, was du getan hast.',
      retryable: false,
    },
    malformedResponse: {
      title: 'Die Antwort ließ sich nicht lesen',
      detail:
        'Die Instanz hat mit etwas geantwortet, aus dem Visious keinen Sinn machen konnte. An der Anfrage war nichts falsch, der nächste Versuch kann also gut ankommen.',
      retryable: true,
    },
  },

  badUrl: {
    title: 'Die Instanz-Adresse funktioniert nicht',
    detail:
      'Visious konnte daraus keine brauchbare Adresse bauen. Prüfe die Instanz unter Einstellungen.',
    retryable: false,
  },

  malformedJson: {
    title: 'Etwas anderes hat geantwortet',
    detail:
      'Die Antwort kam nicht von deiner Instanz. Meist ist es ein Proxy oder ein Anmeldeportal in der Verbindung, die du benutzt.',
    retryable: true,
  },

  emptyResponse: {
    title: 'Die Instanz hat mit nichts geantwortet',
    detail:
      'Sie hat geantwortet, aber ohne jeden Inhalt. Manche Videos tun das jedes Mal, andere gehen beim nächsten Versuch.',
    retryable: true,
  },
};
